// Shape of a workout item, as stored on a `workouts` doc under `items`:
//   { exerciseId, exerciseTitle, mode: 'timed' | 'reps', sets, reps,
//     workSeconds, restSeconds, notes }
//
// The player never walks `items` directly — it walks the flat step queue that
// buildSteps() produces, so that "set 2 of 3" and the rest screens in between
// are just ordinary steps you can skip forwards and backwards through.

export const DEFAULT_ITEM = {
  mode: 'reps',
  sets: 3,
  reps: 10,
  workSeconds: 40,
  restSeconds: 60,
  notes: ''
}

// A sensible guess at how long a rep-based set takes, used only for the
// estimated-duration badge.
const SECONDS_PER_REP = 4

export const emptyWorkoutItem = (exercise) => ({
  ...DEFAULT_ITEM,
  exerciseId: exercise.id,
  exerciseTitle: exercise.title
})

const toInt = (value, fallback = 0) => {
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Expands a workout's items into the flat sequence of screens the player shows.
 * Each set becomes a `work` step, and each gap between sets becomes a `rest`
 * step. The trailing rest is dropped — nobody wants a rest timer after the
 * final set.
 *
 * @param {object} workout        the workout doc
 * @param {object} exercisesById  map of exercise id -> exercise doc
 * @returns {Array} step queue
 */
export const buildSteps = (workout, exercisesById = {}) => {
  const items = Array.isArray(workout?.items) ? workout.items : []
  const steps = []

  items.forEach((item, itemIndex) => {
    const exercise = exercisesById[item.exerciseId] || null
    const sets = Math.max(1, toInt(item.sets, 1))
    const restSeconds = Math.max(0, toInt(item.restSeconds, 0))

    for (let setIndex = 0; setIndex < sets; setIndex++) {
      steps.push({
        type: 'work',
        itemIndex,
        setIndex,
        setCount: sets,
        mode: item.mode === 'timed' ? 'timed' : 'reps',
        seconds: item.mode === 'timed' ? Math.max(1, toInt(item.workSeconds, 30)) : null,
        reps: toInt(item.reps, 0),
        notes: item.notes || '',
        // Fall back to the title stored on the item so a deleted exercise
        // degrades to a named step rather than a blank screen.
        exercise,
        title: exercise?.title || item.exerciseTitle || 'Exercise'
      })

      if (restSeconds > 0) {
        steps.push({
          type: 'rest',
          itemIndex,
          setIndex,
          seconds: restSeconds
        })
      }
    }
  })

  // Drop a trailing rest — the workout is over.
  while (steps.length && steps[steps.length - 1].type === 'rest') {
    steps.pop()
  }

  // Label each rest with what's coming, so the rest screen can preview it.
  steps.forEach((step, index) => {
    if (step.type !== 'rest') return
    const next = steps[index + 1]
    step.nextTitle = next?.title || null
    step.nextIsSameExercise = next ? next.itemIndex === step.itemIndex : false
  })

  return steps
}

/** Total work steps, used for the "3 of 12" counter. */
export const countWorkSteps = (steps) => steps.filter((step) => step.type === 'work').length

/** Estimated workout length in seconds. */
export const estimateSeconds = (workout) => {
  const items = Array.isArray(workout?.items) ? workout.items : []
  return items.reduce((total, item) => {
    const sets = Math.max(1, toInt(item.sets, 1))
    const rest = Math.max(0, toInt(item.restSeconds, 0))
    const work = item.mode === 'timed'
      ? Math.max(1, toInt(item.workSeconds, 30))
      : Math.max(1, toInt(item.reps, 10)) * SECONDS_PER_REP
    return total + sets * work + Math.max(0, sets - 1) * rest
  }, 0)
}

/** Estimated workout length in whole minutes (never less than 1). */
export const estimateMinutes = (workout) => {
  const seconds = estimateSeconds(workout)
  return seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : 0
}

/** 95 -> "1:35" */
export const formatClock = (totalSeconds) => {
  const safe = Math.max(0, Math.round(totalSeconds || 0))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** A workout doc is the new sequence shape; a legacy doc is a bare exercise. */
export const isWorkoutDoc = (data) => Array.isArray(data?.items)
